import { Box, Button, Stack } from "@mui/joy";
import { theme } from "styles/theme";
import React, { useEffect, useRef, useState } from "react";
import mixpanel from "mixpanel-browser";
import useDetectIOS from "hooks/useDetectIOS";
import { useMutation } from "@tanstack/react-query";
import { acquisitionApi } from "api/acquisitionApi";
import { useNavigate } from "react-router-dom";
import CustomTypography from "../../../../components/common/CustomTypography";
import { BottomNextButton } from "../../../../components/common/BottomNextButton";
import { PATH } from "../../../../routes/paths";
import { useRecoilValue } from "recoil";
import { userAtom } from "../../../../recoil/userAtom";
import { resolvePendingMetaSignup } from "analytics/metaSignup";

export function Acquisition_1() {
  const user = useRecoilValue(userAtom);
  const referralCode = user?.referralCode;
  const referralName = user?.referralName || referralCode;
  const referralDisplayName =
    user?.referralDisplayName || user?.referralName || referralCode;
  // 단순할인(discount) 유입은 정산 대상이 아니라 어필리에이트와 채널을 구분해 집계한다.
  const isDiscountReferral = user?.referralCodeType === "discount";

  useDetectIOS();
  const navigate = useNavigate();

  // 레퍼럴 유입 유저는 Mixpanel 프로필(메인 앱 기본 인스턴스)에 유입 채널을 1회 기록
  const referralTrackedRef = useRef(false);
  useEffect(() => {
    if (!referralCode || referralTrackedRef.current) return;
    referralTrackedRef.current = true;
    mixpanel.people.set(
      isDiscountReferral
        ? {
            "유입 채널": "크리에이터 협업",
            "크리에이터 이름": referralName,
          }
        : {
            "유입 채널": "제휴",
            "제휴 유입 채널": referralName,
          }
    );
  }, [referralCode, referralName, isDiscountReferral]);

  const [selectedChannel, setSelectedChannel] = useState<string>();

  const [, setIsAnimating] = useState(false);

  const channelList = [
    { name: "유튜브 (홍승주의 디스턴싱 채널)" },
    { name: "유튜브 (그 외 채널)" },
    { name: "인스타그램" },
    { name: "기타" },
  ];

  const { mutate: submit } = useMutation(
    ({ channelName, detail }: { channelName: string; detail?: string }) =>
      acquisitionApi.submit(channelName, detail),
    {
      onSuccess: (_, { channelName }) => {
        // 인앱 가입의 Meta 가입 이벤트는 유입 채널이 인스타그램일 때만 (보류 플래그 소비)
        resolvePendingMetaSignup(channelName, user);

        if (user?.onboardingType === "f" || user?.onboardingType === "g") {
          navigate("/onboarding_new/introduction/1");
        } else {
          navigate("/onboarding_new/acquisition/3");
        }
      },
    }
  );

  function handleSelected(name: string) {
    setSelectedChannel(name);

    setTimeout(() => {
      setIsAnimating(false); // 애니메이션 종료

      if (name === "기타") {
        navigate(PATH.etc_where);
      } else if (name === "유튜브 (그 외 채널)") {
        navigate(PATH.youtube_channel);
      } else {
        submit({ channelName: name });
      }
    }, 100);
  }

  // 레퍼럴로 진입한 유저는 채널 선택 대신 제휴 환영 화면을 본다.
  // 판별·코드는 백엔드 유저 객체(UserDetail.affiliateReferralCode)에서 내려옴.
  if (referralCode) {
    // 레퍼럴 유입은 채널 선택 없이 유형별 채널로 제출한다 — affiliate="어필리에이트" / discount="크리에이터 협업".
    // 상세(detail)는 두 유형 모두 코드 표시 이름. 제출 성공 시 navigate 는 위 submit mutation 의 onSuccess 가 처리.
    const handleReferralNext = () => {
      submit({
        channelName: isDiscountReferral ? "크리에이터 협업" : "어필리에이트",
        detail: referralName,
      });
    };

    return (
      <>
        <Stack spacing={3} sx={{ paddingBottom: "60px" }}>
          <CustomTypography
            variant="Subtitle"
            fontWeight={700}
            sx={{ color: theme.vars.palette.Content.Highlight }}
          >
            {`${referralDisplayName}님의 추천으로 오셨군요.`}
          </CustomTypography>
          <CustomTypography
            variant="Subtitle"
            fontWeight={500}
            sx={{ color: theme.vars.palette.Content.Strong }}
          >
            {"구독자 전용 우대 혜택과 함께 프로그램을 시작해보세요."}
          </CustomTypography>
        </Stack>
        <BottomNextButton
          disabled={false}
          onClick={handleReferralNext}
          text={"다음"}
        />
      </>
    );
  }

  return (
    <Stack spacing={2} sx={{ paddingBottom: "60px" }}>
      <CustomTypography
        variant="Subtitle"
        fontWeight={700}
        sx={{ color: theme.vars.palette.Content.Highlight }}
      >
        디스턴싱에 오신 것을 환영합니다.
      </CustomTypography>
      <CustomTypography
        variant="Subtitle"
        fontWeight={500}
        sx={{
          color: theme.vars.palette.Content.Strong,
        }}
      >
        무엇을 보고 디스턴싱을 시작하려 결심하셨나요?
      </CustomTypography>

      <Box sx={{ pt: 5, display: "flex", flexDirection: "column" }}>
        <Stack
          direction={"column"}
          sx={{
            width: "100%",
            whiteSpace: "nowrap",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {channelList.map(({ name }, optionIndex) => (
            <Stack key={`channel_option_${name}`} sx={{ flexGrow: 1, gap: 1 }}>
              <Button
                fullWidth
                variant={"SingleSlim"}
                size={"lg"}
                color={selectedChannel === name ? "primary" : "secondary"}
                sx={{
                  transition: "background-color 0.3s ease", // ease 애니메이션 추가
                }}
                value={name}
                onClick={(e) => handleSelected(name)}
              >
                {name}
              </Button>
            </Stack>
          ))}
        </Stack>
      </Box>
      <Box
        sx={{
          // 화면 하단에 고정
          position: "fixed",
          left: 0,
          bottom: 0,
          width: "100%",
          zIndex: 1200,
          background: `linear-gradient(180deg, rgba(246, 249, 250, 0) 0%, ${theme.vars.palette.Background.Level1} 50%)`,
          // TODO home indicator 있는 경우 고려
          p: 2, // 16px
        }}
      ></Box>
    </Stack>
  );
}
